import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe2, Lock, Loader2 } from "lucide-react";
import clsx from "clsx";
import { toast } from "./toaster";

type Props = {
  initialPublic: boolean;
  onToggle: (next: boolean) => Promise<void>;
  compact?: boolean;
  className?: string;
  vibrateTick?: () => void;
  vibrateSuccess?: () => void;
};

export default function PublishToggle({
  initialPublic,
  onToggle,
  compact = false,
  className,
  vibrateTick,
  vibrateSuccess,
}: Props) {
  const [isPublic, setIsPublic] = useState(initialPublic);
  const [busy, setBusy] = useState(false);

  // синхронизация, если проп обновился снаружи
  useEffect(() => setIsPublic(initialPublic), [initialPublic]);

  const label = useMemo(
    () => (isPublic ? "Скрыть из общей галереи" : "Опубликовать"),
    [isPublic]
  );

  async function handleClick() {
    if (busy) return;
    vibrateTick?.();

    const next = !isPublic;

    // оптимистично меняем сразу
    setIsPublic(next);
    setBusy(true);

    try {
      await onToggle(next);
      vibrateSuccess?.();
      toast(next ? "Опубликовано" : "Скрыто", "success");
    } catch (e) {
      // откатываем
      setIsPublic(!next);
      toast("Не удалось изменить статус", "error");
    } finally {
      setBusy(false);
    }
  }

  if (compact) {
    return (
      <button
        type="button"
        aria-label={label}
        onClick={handleClick}
        disabled={busy}
        className={clsx(
          "relative inline-flex h-9 w-9 items-center justify-center rounded-full",
          "border border-white/15 bg-white/5 hover:bg-white/10",
          "transition focus:outline-none focus:ring-2 focus:ring-pink-400/60",
          "disabled:opacity-60 disabled:cursor-not-allowed",
          className
        )}
      >
        {/* свечение при публичности */}
        <AnimatePresence>
          {isPublic && !busy && (
            <motion.span
              layoutId="pubGlow"
              className="absolute inset-0 rounded-full bg-emerald-400/15"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
          )}
        </AnimatePresence>

        {/* иконки */}
        <div className="relative z-10">
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin text-white/80" />
          ) : isPublic ? (
            <motion.span
              key="globe"
              initial={{ rotate: -90, opacity: 0, scale: 0.8 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              <Globe2 className="h-4 w-4 text-emerald-300 drop-shadow" />
            </motion.span>
          ) : (
            <motion.span
              key="lock"
              initial={{ rotate: 90, opacity: 0, scale: 0.8 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              <Globe2 className="h-4 w-4 text-white/80 drop-shadow" />
            </motion.span>
          )}
        </div>

        {/* тултип */}
        <span
          className={clsx(
            "pointer-events-none absolute -left-2 translate-x-[-100%] whitespace-nowrap",
            "rounded-lg border border-white/10 bg-black/70 px-2 py-1 text-xs text-white/80",
            "opacity-0 shadow-xl backdrop-blur-sm transition group-hover:opacity-100",
          )}
        >
          {label}
        </span>
      </button>
    );
  }

  // вариант «кнопка с текстом»
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={clsx(
        "inline-flex items-center gap-2 rounded-xl px-3 h-9",
        "border border-white/10 bg-white/5 hover:bg-white/10",
        "transition text-sm text-white/85",
        "focus:outline-none focus:ring-2 focus:ring-pink-400/60",
        "disabled:opacity-60 disabled:cursor-not-allowed",
        className
      )}
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin text-white/80" />
      ) : isPublic ? (
        <Globe2 className="h-4 w-4 text-emerald-300" />
      ) : (
        <Lock className="h-4 w-4 text-white/80" />
      )}
      <span>{label}</span>
    </button>
  );
}
